import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "../components/ui/avatar";
import {
  Sparkles, AlertTriangle, TrendingUp, Target, HelpCircle,
  CheckCircle, ArrowLeft, MessageSquare, Heart, Plus, Check, X, Lock
} from "lucide-react";
import { LinkedName, LinkedAvatar } from "../components/LinkedProfile";
import { toast } from "sonner";
import InsightConversation from "../components/InsightConversation";

const API = "/api";

const SWOT_META = {
  strengths: { label: "Strengths", icon: Sparkles, bg: "bg-green-50", border: "border-l-green-500", iconBg: "bg-green-100", iconColor: "text-green-600" },
  weaknesses: { label: "Weaknesses", icon: AlertTriangle, bg: "bg-red-50", border: "border-l-red-500", iconBg: "bg-red-100", iconColor: "text-red-600" },
  opportunities: { label: "Opportunities", icon: TrendingUp, bg: "bg-violet-50", border: "border-l-violet-500", iconBg: "bg-violet-100", iconColor: "text-violet-600" },
  threats: { label: "Threats", icon: Target, bg: "bg-amber-50", border: "border-l-amber-500", iconBg: "bg-amber-100", iconColor: "text-amber-600" },
  unsure: { label: "Not sure where it belongs", icon: HelpCircle, bg: "bg-slate-50", border: "border-l-slate-400", iconBg: "bg-slate-200", iconColor: "text-slate-500" },
};

const QUADRANT_OPTIONS = [
  { key: "strengths", label: "Strengths", icon: Sparkles, color: "text-green-600", hoverBg: "hover:bg-green-100" },
  { key: "weaknesses", label: "Weaknesses", icon: AlertTriangle, color: "text-red-600", hoverBg: "hover:bg-red-100" },
  { key: "opportunities", label: "Opportunities", icon: TrendingUp, color: "text-violet-600", hoverBg: "hover:bg-violet-100" },
  { key: "threats", label: "Threats", icon: Target, color: "text-amber-600", hoverBg: "hover:bg-amber-100" },
];

/* ---- Unsure item with quadrant picker ---- */
const UnsureItem = ({ item, idx, addedTo, isKept, onAdd, onKeep, onUnkeep }) => {
  const [picking, setPicking] = useState(false);

  if (addedTo) {
    const meta = SWOT_META[addedTo];
    return (
      <div className="flex items-center gap-2 bg-white rounded-md px-3 py-2 shadow-sm border border-slate-100" data-testid={`unsure-item-${idx}`}>
        <span className="flex-1 text-sm text-slate-700">{item}</span>
        <span className="text-xs text-green-600 font-medium flex items-center gap-1 flex-shrink-0">
          <Check className="w-3 h-3" /> Added to {meta?.label || addedTo}
        </span>
      </div>
    );
  }

  if (isKept) {
    return (
      <div className="flex items-center gap-2 bg-white rounded-md px-3 py-2 shadow-sm border border-slate-100" data-testid={`unsure-item-${idx}`}>
        <span className="flex-1 text-sm text-slate-500 italic">{item}</span>
        <span className="text-xs text-slate-500 font-medium flex items-center gap-1 flex-shrink-0">
          <Lock className="w-3 h-3" /> Kept privately
        </span>
        <button
          onClick={() => onUnkeep("unsure", item)}
          className="text-[11px] text-slate-400 hover:text-slate-600 underline flex-shrink-0"
          data-testid={`unsure-unkeep-${idx}`}
        >
          undo
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-md px-3 py-2 shadow-sm border border-slate-100" data-testid={`unsure-item-${idx}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="flex-1 min-w-[60%] text-sm text-slate-700">{item}</span>
        {!picking ? (
          <>
            <Button
              size="sm"
              onClick={() => setPicking(true)}
              variant="outline"
              className="rounded-full text-xs px-3 h-7 flex-shrink-0 border-violet-300 text-violet-600 hover:bg-violet-50"
              data-testid={`pick-quadrant-btn-${idx}`}
            >
              Add to...
            </Button>
            <Button
              size="sm"
              onClick={() => onKeep("unsure", item)}
              variant="ghost"
              className="rounded-full text-xs px-3 h-7 flex-shrink-0 text-slate-500 hover:bg-slate-100"
              data-testid={`keep-unsure-${idx}`}
            >
              <Lock className="w-3 h-3 mr-1" />
              Keep for myself
            </Button>
          </>
        ) : (
          <button onClick={() => setPicking(false)} className="text-slate-400 hover:text-slate-600 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      {picking && (
        <div className="flex flex-wrap gap-1.5 mt-2 pt-2 border-t border-slate-100" data-testid={`quadrant-picker-${idx}`}>
          {QUADRANT_OPTIONS.map(({ key, label, icon: QIcon, color, hoverBg }) => (
            <button
              key={key}
              onClick={async () => {
                const ok = await onAdd(key, item);
                if (ok) setPicking(false);
              }}
              className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-slate-200 ${hoverBg} transition-colors`}
              data-testid={`pick-${key}-${idx}`}
            >
              <QIcon className={`w-3 h-3 ${color}`} />
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const ViewInsightPage = () => {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [swot, setSwot] = useState(null); // user's current SWOT for duplicate check
  // Locally-mirrored list of "kept privately" items; server-synced on mount
  // via the insight_requests document. Shape: [{category, content, kept_at}]
  const [keptPrivate, setKeptPrivate] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [insightRes, swotRes] = await Promise.all([
        fetch(`${API}/insights/request/${requestId}`, { credentials: "include" }),
        fetch(`${API}/tools/swot`, { credentials: "include" }),
      ]);
      if (insightRes.ok) {
        const payload = await insightRes.json();
        setData(payload);
        setKeptPrivate(payload?.request?.kept_private || []);
      } else {
        toast.error("Failed to load insight");
        navigate("/dashboard");
        return;
      }
      if (swotRes.ok) {
        setSwot(await swotRes.json());
      }
    } catch {
      toast.error("Failed to load insight");
    } finally {
      setLoading(false);
    }
  }, [requestId, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const addSwotItem = async (category, content) => {
    if (!data) return false;
    const { responder } = data;
    try {
      // Items accepted from someone else's insight suggestion default
      // to PRIVATE — the user opted to keep the card but may not want
      // it broadcast yet. They can publish it later via the SWOT tool's
      // per-item privacy toggle.
      const body = { category, content, is_private: true };
      if (responder) {
        // Use responder_id from the request data
        const responderId = data.request?.responder_id;
        if (responderId) {
          body.suggested_by_id = responderId;
          body.suggested_by_name = responder.name;
          body.suggested_by_picture = responder.picture;
        }
      }
      const res = await fetch(`${API}/tools/swot/item`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const newItem = await res.json();
        setSwot(prev => prev ? { ...prev, [category]: [...(prev[category] || []), newItem] } : prev);
        toast.success(`Added to your ${SWOT_META[category]?.label || category} (private)`);
        return true;
      }
    } catch {
      toast.error("Failed to add item");
    }
    return false;
  };

  const isAlreadyAdded = (category, content) => {
    if (!swot) return false;
    return (swot[category] || []).some(
      s => s.content?.toLowerCase() === content.toLowerCase() && !s.deleted
    );
  };

  const findAddedCategory = (content) => {
    if (!swot) return null;
    for (const cat of ["strengths", "weaknesses", "opportunities", "threats"]) {
      if ((swot[cat] || []).some(s => s.content?.toLowerCase() === content.toLowerCase() && !s.deleted)) {
        return cat;
      }
    }
    return null;
  };

  // Kept-privately helpers — identity is (category, content).
  const isKept = (category, content) =>
    (keptPrivate || []).some(
      k => k.category === category && k.content === (content || "").trim()
    );

  const keepForMyself = async (category, content) => {
    try {
      const res = await fetch(
        `${API}/insights/request/${requestId}/suggestions/keep`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ category, content }),
        }
      );
      if (res.ok) {
        const data = await res.json();
        setKeptPrivate(data.kept_private || []);
        toast.success("Kept privately — only you can see this");
        return true;
      }
      toast.error("Could not keep suggestion");
    } catch {
      toast.error("Could not keep suggestion");
    }
    return false;
  };

  const unkeepForMyself = async (category, content) => {
    try {
      const qs = `category=${encodeURIComponent(category)}&content=${encodeURIComponent(content)}`;
      const res = await fetch(
        `${API}/insights/request/${requestId}/suggestions/keep?${qs}`,
        { method: "DELETE", credentials: "include" }
      );
      if (res.ok) {
        const data = await res.json();
        setKeptPrivate(data.kept_private || []);
        return true;
      }
    } catch { /* swallow — undo is best-effort */ }
    return false;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-2 border-violet-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) return null;

  const { request, responder } = data;
  const swotItems = request.suggested_swot_items;
  const comment = request.comment;
  const insightText = request.insight_response;
  const isSwot = request.tool_type === "swot";
  const isValues = request.tool_type === "core_values";
  const suggestedValues = request.suggested_values || [];
  const responderName = responder?.name || "Your connection";
  const votesSummary = data.responder_votes_summary;
  const hasVotes = !!(votesSummary && votesSummary.total > 0);

  const totalSuggestions = swotItems
    ? Object.values(swotItems).reduce((s, arr) => s + (Array.isArray(arr) ? arr.length : 0), 0)
    : 0;

  return (
    <div data-testid="view-insight-page">
      {/* Header */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-4">
        <button
          onClick={() => navigate("/dashboard")}
          className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-4 transition-colors"
          data-testid="back-to-dashboard"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </button>
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "system-ui, sans-serif" }}>
          Insight Received
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          {responderName} shared their perspective on your {request.tool_type.replace("_", " ")}
        </p>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-8 space-y-6">
        {/* Responder Card */}
        <div className="card p-5" data-testid="responder-info">
          <div className="flex items-center gap-4">
            <LinkedAvatar username={responder?.username}>
              <Avatar className="h-12 w-12">
                <AvatarImage src={responder?.picture} alt={responderName} />
                <AvatarFallback className="bg-violet-100 text-violet-600 text-lg">
                  {responderName.charAt(0)}
                </AvatarFallback>
              </Avatar>
            </LinkedAvatar>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "system-ui, sans-serif" }}>
                <LinkedName username={responder?.username} name={responderName} testId="responder-profile-link" />
              </h2>
              {responder?.username && (
                <a
                  href={`/${responder.username}`}
                  className="text-xs text-slate-500 hover:text-violet-600"
                  data-testid="responder-handle"
                >
                  @{responder.username}
                </a>
              )}
              <p className="text-sm text-slate-500">
                Shared insights on your {request.tool_type.replace("_", " ").replace(/\b\w/g, l => l.toUpperCase())}
              </p>
            </div>
            <span className="text-[10px] font-medium text-green-600 bg-green-50 px-2.5 py-1 rounded-full flex items-center gap-1">
              <CheckCircle className="w-3 h-3" /> Completed
            </span>
          </div>
        </div>

        {/* Votes Summary — counts of how the responder reacted to the user's
            existing SWOT items. Shown only when votes were cast as part of
            this insight. Counts only — drill-in lives on the Insights
            Summary page. */}
        {isSwot && hasVotes && (
          <div className="card p-4" data-testid="responder-votes-summary">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle className="w-4 h-4 text-violet-500" />
              <h3 className="text-sm font-semibold text-slate-800">
                {responderName}'s reactions to your SWOT
              </h3>
              <span className="ml-auto text-xs text-slate-400" data-testid="votes-total-count">
                {votesSummary.total} {votesSummary.total === 1 ? "vote" : "votes"}
              </span>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div
                className="rounded-lg bg-green-50 border border-green-100 px-3 py-2.5 text-center"
                data-testid="votes-summary-agree"
              >
                <div className="text-lg font-semibold text-green-700 leading-tight">
                  {votesSummary.agree}
                </div>
                <div className="text-[11px] text-green-700/80 mt-0.5">
                  agreed
                </div>
              </div>
              <div
                className="rounded-lg bg-red-50 border border-red-100 px-3 py-2.5 text-center"
                data-testid="votes-summary-disagree"
              >
                <div className="text-lg font-semibold text-red-700 leading-tight">
                  {votesSummary.disagree}
                </div>
                <div className="text-[11px] text-red-700/80 mt-0.5">
                  disagreed
                </div>
              </div>
              <div
                className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2.5 text-center"
                data-testid="votes-summary-unsure"
              >
                <div className="text-lg font-semibold text-slate-700 leading-tight">
                  {votesSummary.unsure}
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  unsure
                </div>
              </div>
            </div>
            <button
              onClick={() => navigate("/insights/summary")}
              className="text-xs text-violet-600 hover:text-violet-700 mt-3 inline-flex items-center gap-1"
              data-testid="view-vote-details-link"
            >
              See per-item details
              <ArrowLeft className="w-3 h-3 rotate-180" />
            </button>
          </div>
        )}

        {/* SWOT Suggestions — interactive */}
        {isSwot && totalSuggestions > 0 && (
          <div data-testid="swot-suggestions-view">
            <h3 className="text-base font-semibold text-slate-900 mb-3" style={{ fontFamily: "system-ui, sans-serif" }}>
              SWOT Suggestions ({totalSuggestions})
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {["strengths", "weaknesses", "opportunities", "threats"].map((key) => {
                const items = swotItems[key] || [];
                if (items.length === 0) return null;
                const meta = SWOT_META[key];
                const Icon = meta.icon;
                return (
                  <div key={key} className={`card p-4 ${meta.bg} border-l-4 ${meta.border}`} data-testid={`view-swot-${key}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <div className={`w-7 h-7 ${meta.iconBg} rounded-lg flex items-center justify-center`}>
                        <Icon className={`w-3.5 h-3.5 ${meta.iconColor}`} />
                      </div>
                      <h4 className="text-sm font-semibold text-slate-800">{meta.label}</h4>
                      <span className="ml-auto text-xs text-slate-400">{items.length}</span>
                    </div>
                    <div className="space-y-1.5">
                      {items.map((item, idx) => {
                        const added = isAlreadyAdded(key, item);
                        const kept = isKept(key, item);
                        return (
                          <div key={idx} className="flex items-center gap-2 bg-white rounded-md px-3 py-2 shadow-sm border border-slate-100" data-testid={`swot-suggestion-${key}-${idx}`}>
                            <span className={`flex-1 text-sm ${kept ? "text-slate-500 italic" : "text-slate-700"}`}>{item}</span>
                            {added ? (
                              <span className="text-xs text-green-600 font-medium flex items-center gap-1 flex-shrink-0">
                                <Check className="w-3 h-3" /> Added
                              </span>
                            ) : kept ? (
                              <>
                                <span className="text-xs text-slate-500 font-medium flex items-center gap-1 flex-shrink-0">
                                  <Lock className="w-3 h-3" /> Kept privately
                                </span>
                                <button
                                  onClick={() => unkeepForMyself(key, item)}
                                  className="text-[11px] text-slate-400 hover:text-slate-600 underline flex-shrink-0"
                                  data-testid={`unkeep-swot-${key}-${idx}`}
                                >
                                  undo
                                </button>
                              </>
                            ) : (
                              <>
                                <Button
                                  size="sm"
                                  onClick={() => addSwotItem(key, item)}
                                  className="bg-violet-600 hover:bg-violet-700 text-white rounded-full text-xs px-3 h-7 flex-shrink-0"
                                  data-testid={`accept-swot-${key}-${idx}`}
                                >
                                  <Plus className="w-3 h-3 mr-1" />
                                  Add
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => keepForMyself(key, item)}
                                  variant="ghost"
                                  className="rounded-full text-xs px-2 h-7 flex-shrink-0 text-slate-500 hover:bg-slate-100"
                                  data-testid={`keep-swot-${key}-${idx}`}
                                  title="Acknowledge without adding to your SWOT"
                                >
                                  <Lock className="w-3 h-3 mr-1" />
                                  Keep
                                </Button>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Unsure items — with quadrant picker */}
            {(swotItems.unsure || []).length > 0 && (
              <div className={`card p-4 mt-3 ${SWOT_META.unsure.bg} border-l-4 ${SWOT_META.unsure.border}`} data-testid="view-swot-unsure">
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-7 h-7 ${SWOT_META.unsure.iconBg} rounded-lg flex items-center justify-center`}>
                    <HelpCircle className={`w-3.5 h-3.5 ${SWOT_META.unsure.iconColor}`} />
                  </div>
                  <h4 className="text-sm font-semibold text-slate-800">{SWOT_META.unsure.label}</h4>
                  <span className="ml-auto text-xs text-slate-400">{swotItems.unsure.length}</span>
                </div>
                <p className="text-xs text-slate-500 mb-2">
                  {responderName} wasn't sure where to place these. Pick the right quadrant to add them.
                </p>
                <div className="space-y-2">
                  {swotItems.unsure.map((item, idx) => (
                    <UnsureItem
                      key={idx}
                      item={item}
                      idx={idx}
                      addedTo={findAddedCategory(item)}
                      isKept={isKept("unsure", item)}
                      onAdd={addSwotItem}
                      onKeep={keepForMyself}
                      onUnkeep={unkeepForMyself}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Suggested Values (for core_values) */}
        {isValues && suggestedValues.length > 0 && (
          <div data-testid="suggested-values-view">
            <h3 className="text-base font-semibold text-slate-900 mb-3" style={{ fontFamily: "system-ui, sans-serif" }}>
              Suggested Values ({suggestedValues.length})
            </h3>
            <div className="space-y-2">
              {suggestedValues.map((name, idx) => (
                <div key={idx} className="card p-3 flex items-center gap-3">
                  <Heart className="w-4 h-4 text-red-400 flex-shrink-0" />
                  <span className="text-sm font-medium text-slate-800">{name}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* General Comment */}
        {comment && (
          <div className="card p-4 bg-slate-50 border-l-4 border-l-violet-400" data-testid="general-comment-view">
            <div className="flex items-center gap-2 mb-2">
              <MessageSquare className="w-4 h-4 text-violet-500" />
              <h4 className="text-sm font-semibold text-slate-800">General Comment</h4>
            </div>
            <p className="text-sm text-slate-700 leading-relaxed">{comment}</p>
          </div>
        )}

        {/* Written Insight Response */}
        {insightText && insightText !== "(SWOT suggestions provided)" && insightText !== "(Reactions provided)" && (
          <div className="card p-4" data-testid="insight-text-view">
            <h4 className="text-sm font-semibold text-slate-800 mb-2">Written Feedback</h4>
            <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{insightText}</p>
          </div>
        )}

        {/* No suggestions at all — fallback. Hidden when votes-only insight,
            since the votes-summary block above already conveys what the
            responder did. */}
        {totalSuggestions === 0 && suggestedValues.length === 0 && !comment && !hasVotes && (!insightText || insightText === "(SWOT suggestions provided)" || insightText === "(Reactions provided)") && (
          <div className="card p-8 text-center">
            <p className="text-slate-500">No detailed suggestions were provided for this insight.</p>
          </div>
        )}

        {/* Link to full SWOT Analysis */}
        {isSwot && (
          <Button
            variant="outline"
            onClick={() => navigate("/tools/swot")}
            className="w-full border-violet-200 text-violet-600 hover:bg-violet-50"
            data-testid="view-full-swot-btn"
          >
            View your full SWOT Analysis
          </Button>
        )}

        {/* Conversation Thread */}
        <InsightConversation
          requestId={requestId}
          collapsed={false}
          responderName={responderName}
          responderPicture={responder?.picture}
          responderUsername={responder?.username}
        />
      </div>
    </div>
  );
};

export default ViewInsightPage;
